import classNames from "classnames";
import Button from "kustom-front/src/components/atoms/Button";
import React, { FunctionComponent, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import CartButtonSpinner from "@Components/atoms/cart/CartButtonSpinner";
import CartAddressFormBilling from "@Components/molecules/cart/CartAddressFormBilling";
import CartMondialRelayOverview from "@Components/molecules/cart/CartMondialRelayOverview";
import useCartContext from "@Hooks/useCartContext";
import useNotificationsContext from "@Hooks/useNotificationsContext";
import { countries as countriesList } from "@Samples/mocks/cart";
import { BillingAddress, DeliveryAddress, MondialRelayLocation } from "@Types/addresses";
import { ShippingModes } from "@Types/shipping";
import "./style.scss";

interface Props extends ContainerProps {
  billingAddress: BillingAddress;
  deliveryAddress: DeliveryAddress;
  mondialRelayShippingOptions: ShippingModes | undefined;
}

interface ContainerProps {
  disabled: boolean;
  mondialRelaySelectedLocation: MondialRelayLocation | undefined;
  onSelectLocation: (location?: MondialRelayLocation) => void;
  onSubmitAddress: ({
    billing,
    delivery,
  }: {
    billing: Partial<BillingAddress>;
    delivery: Partial<DeliveryAddress>;
  }) => void;
}

export const CartMondialRelay: FunctionComponent<Props> = ({
  billingAddress,
  deliveryAddress,
  disabled,
  mondialRelaySelectedLocation,
  mondialRelayShippingOptions,
  onSelectLocation,
  onSubmitAddress,
}) => {
  const [billingCity, setBillingCity] = useState<string | undefined>(billingAddress.city || "");
  const [billingCompany, setBillingCompany] = useState<string | undefined>(
    billingAddress.company || ""
  );
  const [billingCountry, setBillingCountry] = useState<string | undefined>(billingAddress.country);
  const [billingCountryTag, setBillingCountryTag] = useState<string | undefined>(
    billingAddress.countryTag
  );
  const [billingDetails, setBillingDetails] = useState<string | undefined>(
    billingAddress.details || ""
  );
  const [billingFirstName, setBillingFirstName] = useState<string | undefined>(
    billingAddress.firstName || ""
  );
  const [billingLastName, setBillingLastName] = useState<string | undefined>(
    billingAddress.lastName || ""
  );
  const [billingPostcode, setBillingPostcode] = useState<string | undefined>(
    billingAddress.postcode || ""
  );
  const [billingStreet, setBillingStreet] = useState<string | undefined>(
    billingAddress.street || ""
  );
  const [billingVatNumber, setBillingVatNumber] = useState<string | undefined>(
    billingAddress.vatNumber || ""
  );

  const [submittedForm, setSubmittedForm] = useState<boolean>(false);

  const intl = useIntl();
  const { dispatchNotification } = useNotificationsContext();

  const billing = {
    city: billingCity,
    company: billingCompany,
    country: billingCountry,
    countryTag: billingCountryTag,
    details: billingDetails,
    firstName: billingFirstName,
    lastName: billingLastName,
    postcode: billingPostcode,
    street: billingStreet,
    vatNumber: billingVatNumber,
  };

  const handleSubmitAddress = () => {
    setSubmittedForm(true);
    if (
      !mondialRelaySelectedLocation ||
      !deliveryAddress.firstName ||
      !deliveryAddress.lastName ||
      !deliveryAddress.email ||
      !billing.city ||
      !billing.firstName ||
      !billing.lastName ||
      !billing.postcode ||
      !billing.street
    ) {
      dispatchNotification({
        code: "CFE-002",
        type: "addNotificationByCode",
      });
      return;
    }

    const selectedCountry = countriesList.find(
      (value) => value.tag === mondialRelaySelectedLocation.countryTag2
    );

    onSubmitAddress({
      billing,
      delivery: {
        city: mondialRelaySelectedLocation.city,
        country: selectedCountry && selectedCountry.name,
        countryTag: selectedCountry && selectedCountry.tag,
        details: mondialRelaySelectedLocation.details,
        email: deliveryAddress.email,
        firstName: deliveryAddress.firstName,
        lastName: deliveryAddress.lastName,
        mondialRelayNumber: mondialRelaySelectedLocation.mondialRelayNumber,
        phone: deliveryAddress.phone,
        postcode: mondialRelaySelectedLocation.postcode,
        street: mondialRelaySelectedLocation.street,
        tag: "mondial_relay",
      },
    });
  };

  const handleChangeBilling = useCallback((field: string, value: string) => {
    switch (field) {
      case "firstName":
        setBillingFirstName(value);
        break;
      case "lastName":
        setBillingLastName(value);
        break;
      case "city":
        setBillingCity(value);
        break;
      case "company":
        setBillingCompany(value);
        break;
      case "country":
        setBillingCountry(value);
        break;
      case "countryTag":
        setBillingCountryTag(value);
        break;
      case "postcode":
        setBillingPostcode(value);
        break;
      case "details":
        setBillingDetails(value);
        break;
      case "street":
        setBillingStreet(value);
        break;
      case "vatNumber":
        setBillingVatNumber(value);
        break;
      default:
        return;
    }
  }, []);

  return (
    <div
      className={classNames("CartMondialRelay", {
        submitted: submittedForm,
      })}
    >
      {mondialRelaySelectedLocation !== undefined && mondialRelayShippingOptions && (
        <>
          <CartMondialRelayOverview
            mondialRelayLocation={mondialRelaySelectedLocation}
            mondialRelayShippingOptions={mondialRelayShippingOptions}
            onChangeLocation={onSelectLocation}
          />
          <CartAddressFormBilling
            city={billingCity}
            company={billingCompany}
            countries={[]}
            country={billingCountry}
            details={billingDetails}
            firstName={billingFirstName}
            lastName={billingLastName}
            onChange={handleChangeBilling}
            postcode={billingPostcode}
            street={billingStreet}
            vatNumber={billingVatNumber}
          />
          <div className="CartMondialRelay-buttonContainer">
            <Button disabled={disabled} onClick={handleSubmitAddress} type="accent">
              {disabled ? (
                <CartButtonSpinner />
              ) : (
                intl.formatMessage({ id: "cheerz_CartAddressForm_order" })
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const CartMondialRelayContainer: FunctionComponent<ContainerProps> = ({
  disabled,
  mondialRelaySelectedLocation,
  onSelectLocation,
  onSubmitAddress,
}) => {
  const { addresses, cart, user } = useCartContext();

  return (
    <CartMondialRelay
      billingAddress={addresses.billing}
      deliveryAddress={{ ...addresses.delivery, email: user.email || addresses.delivery.email }}
      disabled={disabled}
      mondialRelaySelectedLocation={mondialRelaySelectedLocation}
      mondialRelayShippingOptions={cart.shippingOptions.mondialRelay}
      onSelectLocation={onSelectLocation}
      onSubmitAddress={onSubmitAddress}
    />
  );
};

export default CartMondialRelayContainer;
